import { componentsOptions, getModalTabs } from "@/utils/dashboardUtils";
import { Button, Form, Tabs } from "antd";
import dayjs from "dayjs";
import moment from "moment";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { MdOutlineKeyboardBackspace } from "react-icons/md";

import AppearanceTab from "@/pages/Dashboard/redesign/tabs/AppearanceTab";
import AxisTab from "@/pages/Dashboard/redesign/tabs/AxisTab";
import BasicTab from "@/pages/Dashboard/redesign/tabs/BasicTab";
import DataTab from "@/pages/Dashboard/redesign/tabs/DataTab";
import EditorTab from "@/pages/Dashboard/redesign/tabs/EditorTab";
import GaugeTab from "@/pages/Dashboard/redesign/tabs/GaugeTab";
import CounterTab from "@/pages/Dashboard/redesign/tabs/MetricTab";
import TimeFrameTab from "@/pages/Dashboard/redesign/tabs/TimeFrameTab";
import ReportPreview from "../ReportPreview";

const tabItemsInit = [
  {
    key: "1",
    label: "Basic",
    children: "Content of Tab Pane 1",
  },
];

const onFinishFailed = (values) => {
  // eslint-disable-next-line no-console
  console.log("error", values);
};

function CustomReportAddItemModal({
  show,
  setShow,
  mode = "edit",
  editItem,
  setEditItem,
  gridData,
  setGridData,
  data,
  setData,
  iframe,
  setIframe,
  setComponentModal,
}) {
  const [form] = Form.useForm();

  // state
  const [selectedComponent, setSelectedComponent] = useState({});
  const [tabItems, setTabItems] = useState(tabItemsInit);
  const [selectedTab, setSelectedTab] = useState("basic");

  // others
  const tabProps = { editItem, setEditItem, form, setData, iframe, setIframe, data, type: "report" };
  const tabBody = {
    basic: <BasicTab {...tabProps} />,
    data: <DataTab {...tabProps} />,
    appearance: <AppearanceTab {...tabProps} />,
    axis: <AxisTab {...tabProps} />,
    timeFrame: <TimeFrameTab {...tabProps} />,
    gauge: <GaugeTab {...tabProps} />,
    counter: <CounterTab {...tabProps} />,
    editor: <EditorTab {...tabProps} />,
  };

  // effects
  useEffect(() => {
    if (show && editItem) {
      const values = { ...editItem };
      if (editItem?.timeRange) {
        values.timeRange = editItem?.timeRange?.map((item) => dayjs(item, "YYYY-MM-DD HH:mm"));
      }
      // console.log({ values, editItem });
      form.setFieldsValue(values);
    }
  }, [editItem, show, form]);

  useEffect(() => {
    const component = componentsOptions.find((component) => component?.value === editItem?.component) || {};
    setSelectedComponent(component);
    const tabs = getModalTabs(component);
    if (Array.isArray(tabs) && tabs.length > 0) {
      // console.log({ tabs });
      setTabItems(tabs);
      setSelectedTab(tabs[0].key);
    }
  }, [editItem?.component]);

  // handler
  const handleClose = () => {
    setShow(false);
    setEditItem({});
    form.resetFields();
  };
  const handleTabChange = (key) => {
    setSelectedTab(key);
  };

  const handleCreate = (data) => {
    setGridData((prev) => [...prev, data]);
    setShow(false);
  };

  // have to change according new data
  const handleEdit = (data) => {
    const updated = gridData.map((item) => {
      if (item._id === data._id) {
        item = { ...item, ...data };
      }
      return item;
    });
    setGridData(updated);
    setShow(false);
  };

  const onFinish = () => {
    const data = { ...editItem };
    if (mode === "edit") {
      handleEdit(data);
    } else {
      handleCreate(data);
    }
    setEditItem({});
  };

  const backHandler = () => {
    setShow(false);
    setComponentModal(true);
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} centered size="" dialogClassName="dashboard-modal" enforceFocus={false}>
        {/* header */}
        <Modal.Header closeButton>
          <div className="d-flex align-items-center justify-content-between w-100">
            <div>
              <Button color="default" variant="text" size="small" onClick={backHandler}>
                <MdOutlineKeyboardBackspace className="fs-4" />
              </Button>
            </div>
            <Modal.Title className="fs-6">{`Edit ${selectedComponent.label}`}</Modal.Title>
            <div className="opacity-0"></div>
          </div>
        </Modal.Header>

        {/* preview */}
        <Modal.Body className="p-4 bg-body-tertiary">
          <div className="mb-1">Live Preview</div>
          <div
            // className="bg-white p-3 shadow-sm rounded-3"
            className={`dashboardTiles border-0 p-3 ${editItem.isBorderHidden ? "" : "withBorder"}`}
            style={{ height: selectedComponent?.minHeight || 100 }}
          >
            <ReportPreview
              {...{
                iframe,
                setIframe,
                item: { ...editItem },
              }}
            />
          </div>
        </Modal.Body>
        {/* body */}
        <Modal.Body className="p-4">
          <Tabs activeKey={selectedTab} items={tabItems} onChange={handleTabChange} />

          <Form
            layout="vertical"
            name="dashboard"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            form={form}
            scrollToFirstError={true}
            onValuesChange={(_, values) => {
              const modified = { ...values };
              if (values?.timeRange?.length) {
                modified.timeRange = values?.timeRange?.map((time) => moment(new Date(time)).format("YYYY-MM-DD HH:mm")) || [];
              }
              // console.log("item", values, modified);
              return setEditItem((pre) => ({ ...pre, ...modified }));
            }}
          >
            {/* body */}
            {editItem.component && selectedTab && tabBody[selectedTab]}

            {/* footer */}
            <div className="d-flex align-items-center justify-content-end gap-2 pt-2">
              <Button type="primary" htmlType="submit">
                {mode === "edit" ? "Update" : "Create"}
              </Button>
              <Button type="primary" className="bg-secondary" onClick={handleClose} htmlType="button">
                Cancel
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default CustomReportAddItemModal;

CustomReportAddItemModal.propTypes = {
  show: PropTypes.bool,
  setShow: PropTypes.func,
  mode: PropTypes.string,
  editItem: PropTypes.object,
  setEditItem: PropTypes.func,
  gridData: PropTypes.array,
  setGridData: PropTypes.func,
  data: PropTypes.object,
  setData: PropTypes.func,
  iframe: PropTypes.string,
  setIframe: PropTypes.func,
  setComponentModal: PropTypes.func,
};
